// Budgets, price lists and FX rates (04 §Approvals, runs, agents: `budgets`, `price-lists`;
// 11 §Budget hierarchy, §Controls, §Money representation). M3-01 contracts: handlers are M3-04
// on FIN-01's pure `packages/domain-finance`. Finance controls govern every cost, so they are
// base plan (`core`); budgets are `company#manager` (05 §Finance).
import { z } from "zod";
import { Budget, BudgetIncident, FxRate, PriceList } from "../agent-rows";
import {
  BudgetPeriodType,
  BudgetState,
  BudgetSubjectKind,
  Currency,
  IngestKeySource,
  PriceUnit,
} from "../enums";
import {
  DecimalInput,
  IsoDate,
  LIMITS,
  MicrosAmount,
  MinorAmount,
  PeriodKey,
  Timestamp,
  Uuid,
} from "../primitives";
import { AgentKey } from "../rows";
import { CompanyItemParams, CompanyParams, PageQuery, page } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const core = { feature: "core" } as const;

/** A budget level (11): `period_key` with every period type but `standing`. */
export const BudgetInput = z
  .strictObject({
    subject_kind: BudgetSubjectKind,
    subject_id: Uuid,
    period_type: BudgetPeriodType,
    period_key: PeriodKey.nullable().optional(),
    amount_minor: MinorAmount.min(0).describe(
      "Integer minor units of `currency` (cents for USD: 500 = $5.00)",
    ),
    currency: Currency,
    soft_thresholds: z.array(DecimalInput).max(5).optional().describe("Fractions; default [0.8]"),
    hard_cap_minor: MinorAmount.min(0).nullable().optional(),
    inherit: z.boolean().optional(),
    /**
     * DE-BUDGET-PACE: a DE's own (holder) budget without a hard cap is capped at its amount
     * (its reserve stops new runs); true keeps it an alert only. Default false.
     */
    alert_only: z.boolean().optional(),
    /**
     * BUDGET-CAP: the plain word for `alert_only` — `alert` (warns, never stops) or `cap` (new
     * runs stop at the limit). `alert_only` wins when both are sent.
     */
    mode: z.enum(["alert", "cap"]).optional(),
    /** BUDGET-ROLLOVER: create the next period's budget when this one ends. Default true. */
    repeat_monthly: z.boolean().optional(),
  })
  .meta({ id: "BudgetInput" });
export const BudgetPatch = BudgetInput.omit({
  subject_kind: true,
  subject_id: true,
  period_type: true,
})
  .partial()
  .meta({ id: "BudgetPatch" });

export const BudgetListQuery = PageQuery.extend({
  subject_kind: BudgetSubjectKind.optional(),
  subject_id: Uuid.optional(),
  state: BudgetState.optional(),
});

/** A budget with its actuals, reservations and open incidents for its period. */
export const BudgetDetail = Budget.extend({
  actual_micros: MicrosAmount,
  reserved_micros: MicrosAmount,
  incidents: z.array(BudgetIncident),
}).meta({ id: "BudgetDetail" });

export const PriceListQuery = PageQuery.extend({
  unit: PriceUnit.optional(),
  model: z.string().max(LIMITS.key).optional(),
});
/**
 * PRICE-TIERS: an optional second rate for calls whose prompt is above a threshold (tokens per
 * call), in the same `per_units`; both fields or neither.
 */
const Tier = {
  tier_above_tokens: z.int().min(1).max(1_000_000_000).optional(),
  tier_price_micros: MicrosAmount.min(0).optional(),
};
const bothOrNeither = (b: {
  tier_above_tokens?: number | undefined;
  tier_price_micros?: number | undefined;
}) => (b.tier_above_tokens === undefined) === (b.tier_price_micros === undefined);
const TIER_PAIR = {
  message: "tier_above_tokens and tier_price_micros come together",
  path: ["tier_price_micros"],
};

/** Company prices only; platform and agency rows arrive mirrored from the control plane. */
export const PriceCreate = z
  .strictObject({
    unit: PriceUnit,
    model: z.string().max(LIMITS.key).nullable().optional(),
    price_micros: MicrosAmount.min(0),
    /** HTVA-RUN-FIXES: the units `price_micros` is for (default 1; 1,000,000 = per million). */
    per_units: z.int().min(1).max(1_000_000_000).optional(),
    ...Tier,
    currency: Currency,
    effective_from: IsoDate,
  })
  .refine(bothOrNeither, TIER_PAIR)
  .meta({ id: "PriceCreate" });

/**
 * HTVA-RUN-FIXES: a platform cost price (what a provider charges us; `cp.price_catalog`, scope
 * platform), written by a platform super admin, audited, mirrored into every company. Append-only:
 * a change is a new row effective today or later.
 */
export const CostPriceCreate = z
  .strictObject({
    unit: PriceUnit,
    model: z.string().trim().min(1).max(128).nullable().optional(),
    price_micros: MicrosAmount.min(0),
    per_units: z.int().min(1).max(1_000_000_000).optional(),
    ...Tier,
    currency: Currency,
    effective_from: IsoDate,
  })
  .refine(bothOrNeither, TIER_PAIR)
  .meta({ id: "CostPriceCreate" });

export const CostPrice = z
  .object({
    id: Uuid,
    unit: PriceUnit,
    model: z.string().nullable(),
    price_micros: MicrosAmount,
    per_units: z.int().min(1),
    tier_above_tokens: z.int().min(1).nullable(),
    tier_price_micros: MicrosAmount.nullable(),
    currency: Currency,
    effective_from: IsoDate,
    created_by_user_id: Uuid.nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "CostPrice" });
export type CostPrice = z.infer<typeof CostPrice>;

export const FxRateParams = z.object({
  co: Uuid,
  period: PeriodKey,
  pair: z
    .string()
    .max(7)
    .regex(/^[A-Z]{3}-[A-Z]{3}$/)
    .describe("`EUR-USD`"),
});
export const FxRatePut = z
  .strictObject({ rate: DecimalInput, source: z.string().max(LIMITS.name).optional() })
  .meta({ id: "FxRatePut" });

/**
 * D-16 (M3-04): a source-scoped ingest key posts `time` items of one time source (WorkPulse, a
 * tracker, …) through `POST ingest/events` with the internal channel's rules: people and seats are
 * resolved through `external_identity` of that source, nothing else is accepted. It holds exactly
 * `ingest:time` and is bound to the person who issued it (their membership; no seat), and stops
 * working when that membership ends.
 */
export const SourceKeyCreate = z
  .strictObject({ source: IngestKeySource, expires_at: Timestamp.nullable().optional() })
  .meta({ id: "SourceKeyCreate" });
/** The plaintext (`cosk_in_…`) is returned once, to the creator only, and never stored. */
export const SourceKeyCreated = z
  .object({ key: AgentKey, plaintext: z.string().startsWith("cosk_in_") })
  .meta({ id: "SourceKeyCreated" });

export const financeRoutes = {
  "budgets.list": defineRoute({
    ...core,
    id: "budgets.list",
    method: "get",
    path: `${base}/budgets`,
    tag: "budgets",
    summary: "Budgets of the hierarchy (company → team → seat → holder → agent)",
    access: "manager",
    params: CompanyParams,
    query: BudgetListQuery,
    response: { status: 200, schema: page(Budget) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "budgets.create": defineRoute({
    ...core,
    id: "budgets.create",
    method: "post",
    path: `${base}/budgets`,
    tag: "budgets",
    summary: "Set a budget on one level for a period (or standing)",
    access: "manager",
    params: CompanyParams,
    body: BudgetInput,
    idempotency: "optional",
    response: { status: 201, schema: Budget },
    errors: ["conflict"],
    emits: ["budget.changed"],
  }),
  "budgets.get": defineRoute({
    ...core,
    id: "budgets.get",
    method: "get",
    path: `${base}/budgets/{id}`,
    tag: "budgets",
    summary: "A budget with its actuals, reservations and incidents",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: BudgetDetail },
    errors: [],
    emits: [],
  }),
  "budgets.update": defineRoute({
    ...core,
    id: "budgets.update",
    method: "patch",
    path: `${base}/budgets/{id}`,
    tag: "budgets",
    summary: "Change amount, thresholds, hard cap or inheritance",
    access: "manager",
    params: CompanyItemParams,
    body: BudgetPatch,
    response: { status: 200, schema: Budget },
    errors: [],
    emits: ["budget.changed", "digital_employee.resumed"],
  }),
  "budgets.archive": defineRoute({
    ...core,
    id: "budgets.archive",
    method: "post",
    path: `${base}/budgets/{id}/archive`,
    tag: "budgets",
    summary: "Archive a budget (its level inherits again)",
    access: "manager",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: Budget },
    errors: ["conflict"],
    emits: ["budget.changed"],
  }),
  // ACTIONS: an archived budget active again (409 while its level has another active one).
  "budgets.unarchive": defineRoute({
    ...core,
    id: "budgets.unarchive",
    method: "post",
    path: `${base}/budgets/{id}/unarchive`,
    tag: "budgets",
    summary: "Restore an archived budget (409 while its level has an active one for the period)",
    access: "manager",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: Budget },
    errors: ["conflict"],
    emits: ["budget.changed"],
  }),
  "priceLists.list": defineRoute({
    ...core,
    id: "priceLists.list",
    method: "get",
    path: `${base}/price-lists`,
    tag: "budgets",
    summary: "Effective prices (platform, agency and company rows)",
    access: "manager",
    params: CompanyParams,
    query: PriceListQuery,
    response: { status: 200, schema: page(PriceList) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "priceLists.create": defineRoute({
    ...core,
    id: "priceLists.create",
    method: "post",
    path: `${base}/price-lists`,
    tag: "budgets",
    summary: "Add a company price, effective from a date",
    access: "manager",
    params: CompanyParams,
    body: PriceCreate,
    idempotency: "optional",
    response: { status: 201, schema: PriceList },
    errors: ["conflict"],
    emits: ["price_list.changed"],
  }),
  // HTVA-RUN-FIXES: the platform cost price list (what providers charge us per model), the
  // platform super admin's; every company reads it through its mirror, a company row overrides.
  "operator.costPrices.list": defineRoute({
    ...core,
    id: "operator.costPrices.list",
    method: "get",
    path: "/v1/operator/cost-prices",
    tag: "budgets",
    summary: "The platform cost price list (per model and unit), newest first",
    access: "operator",
    response: { status: 200, schema: z.object({ data: z.array(CostPrice) }) },
    errors: [],
    emits: [],
  }),
  "operator.costPrices.create": defineRoute({
    ...core,
    id: "operator.costPrices.create",
    method: "post",
    path: "/v1/operator/cost-prices",
    tag: "budgets",
    summary:
      "A new platform cost price, effective today or later (audited; cells get it by mirror)",
    access: "operator",
    body: CostPriceCreate,
    idempotency: "optional",
    response: { status: 201, schema: CostPrice },
    errors: ["conflict", "forbidden"],
    emits: ["billing.price_changed"],
  }),
  "fxRates.list": defineRoute({
    ...core,
    id: "fxRates.list",
    method: "get",
    path: `${base}/fx-rates`,
    tag: "budgets",
    summary: "FX rates per period",
    access: "manager",
    params: CompanyParams,
    query: PageQuery,
    response: { status: 200, schema: page(FxRate) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "sourceKeys.list": defineRoute({
    ...core,
    id: "sourceKeys.list",
    method: "get",
    path: `${base}/source-keys`,
    tag: "imports",
    summary: "Source-scoped ingest keys (time sources)",
    access: "manager",
    params: CompanyParams,
    query: PageQuery,
    response: { status: 200, schema: page(AgentKey) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "sourceKeys.create": defineRoute({
    ...core,
    id: "sourceKeys.create",
    method: "post",
    path: `${base}/source-keys`,
    tag: "imports",
    summary: "Issue a source-scoped ingest key (time items of one source; plaintext once)",
    access: "manager",
    params: CompanyParams,
    body: SourceKeyCreate,
    idempotency: "required",
    response: { status: 201, schema: SourceKeyCreated },
    errors: ["key_unbound"],
    emits: ["agent_key.created"],
  }),
  "sourceKeys.revoke": defineRoute({
    ...core,
    id: "sourceKeys.revoke",
    method: "post",
    path: `${base}/source-keys/{id}/revoke`,
    tag: "imports",
    summary: "Revoke a source-scoped ingest key",
    access: "manager",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: AgentKey },
    errors: ["conflict"],
    emits: ["agent_key.revoked"],
  }),
  "fxRates.put": defineRoute({
    ...core,
    id: "fxRates.put",
    method: "put",
    path: `${base}/fx-rates/{period}/{pair}`,
    tag: "budgets",
    summary: "Set the rate of a currency pair for a period",
    access: "manager",
    params: FxRateParams,
    body: FxRatePut,
    response: { status: 200, schema: FxRate },
    errors: [],
    emits: ["fx_rate.set"],
  }),
} as const;
